import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Ban, Car, LogIn, Plus, Search, Trash2, UserCheck } from "lucide-react";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Tabs, TabsList, TabsTrigger } from "../ui/tabs";
import { Textarea } from "../ui/textarea";
import { useApiMessages } from "../../hooks/useApiMessages";
import { formatCedula, formatPhoneNumber } from "../../lib/utils";
import { PhotoInput } from "./VisitorShared";
import { visitorApi } from "./visitorUtils";
import { printVisitLabel } from "./VisitLabel";

export const ID_TYPES = ["national_id", "passport", "driver_license", "other"];
export const ITEM_CATEGORIES = ["tool", "electronic", "package", "document", "other"];
export const INCIDENT_TYPES = [
  "unauthorized_access",
  "denied_entry",
  "invalid_qr",
  "suspicious_behavior",
  "item_discrepancy",
  "property_damage",
  "overstay",
  "other",
];
export const SEVERITIES = ["low", "medium", "high", "critical"];

const EMPTY_VISITOR = { full_name: "", id_type: "national_id", id_number: "", visitor_company: "", phone: "", email: "", photo_base64: null };

const Field = ({ label, children, className }) => (
  <div className={className}>
    <Label className="mb-1 block">{label}</Label>
    {children}
  </div>
);

/** Búsqueda de visitantes registrados (nombre, identificación, empresa o teléfono). */
export const VisitorSearch = ({ companyId, onSelect }) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);

  useEffect(() => {
    if (query.trim().length < 2) {
      setResults([]);
      return;
    }
    const handle = setTimeout(() => {
      visitorApi
        .get("/people", { search: query, company_id: companyId, limit: 15 })
        .then(setResults)
        .catch(() => setResults([]));
    }, 300);
    return () => clearTimeout(handle);
  }, [query, companyId]);

  return (
    <div className="space-y-2">
      <div className="relative">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          className="pl-9"
          placeholder={t("visitors.access.searchVisitor")}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          autoFocus
        />
      </div>
      {results.length > 0 && (
        <div className="border rounded-lg divide-y max-h-60 overflow-y-auto">
          {results.map((v) => (
            <button
              type="button"
              key={v.id}
              onClick={() => onSelect(v)}
              className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center justify-between gap-2"
            >
              <div className="min-w-0">
                <p className="font-medium text-slate-900 truncate">{v.full_name}</p>
                <p className="text-xs text-slate-500 truncate">
                  {[v.id_number, v.visitor_company, v.phone].filter(Boolean).join(" · ")}
                </p>
              </div>
              <div className="flex gap-1 flex-shrink-0">
                {v.is_frequent && <Badge variant="secondary">{t("visitors.common.frequent")}</Badge>}
                {v.is_blocked && <Badge variant="destructive">{t("visitors.common.blocked")}</Badge>}
              </div>
            </button>
          ))}
        </div>
      )}
      {query.trim().length >= 2 && results.length === 0 && (
        <p className="text-sm text-slate-500">{t("visitors.common.noResults")}</p>
      )}
    </div>
  );
};

/** Campos del visitante nuevo. */
export const VisitorFields = ({ value, onChange, withPhoto = true }) => {
  const { t } = useTranslation();
  const set = (k, v) => onChange({ ...value, [k]: v });
  const isCedula = (value.id_type || "national_id") === "national_id";
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <Field label={`${t("visitors.common.fullName")} *`} className="sm:col-span-2">
        <Input value={value.full_name} onChange={(e) => set("full_name", e.target.value)} />
      </Field>
      <Field label={t("visitors.common.idType")}>
        <Select
          value={value.id_type || "national_id"}
          onValueChange={(v) =>
            onChange({ ...value, id_type: v, id_number: v === "national_id" ? formatCedula(value.id_number) : value.id_number })
          }
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {ID_TYPES.map((k) => (
              <SelectItem key={k} value={k}>
                {t(`visitors.idTypes.${k}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>
      <Field label={t("visitors.common.idNumber")}>
        <Input
          value={value.id_number || ""}
          inputMode={isCedula ? "numeric" : undefined}
          placeholder={isCedula ? "000-0000000-0" : undefined}
          onChange={(e) => set("id_number", isCedula ? formatCedula(e.target.value) : e.target.value)}
        />
      </Field>
      <Field label={t("visitors.common.visitorCompany")}>
        <Input value={value.visitor_company || ""} onChange={(e) => set("visitor_company", e.target.value)} />
      </Field>
      <Field label={t("visitors.common.phone")}>
        <Input
          type="tel"
          inputMode="tel"
          placeholder="(809) 555-1234"
          value={value.phone || ""}
          onChange={(e) => set("phone", formatPhoneNumber(e.target.value))}
        />
      </Field>
      <Field label={t("visitors.common.email")} className="sm:col-span-2">
        <Input type="email" value={value.email || ""} onChange={(e) => set("email", e.target.value)} />
      </Field>
      {withPhoto && (
        <div className="sm:col-span-2">
          <PhotoInput label={t("visitors.common.photo")} value={value.photo_base64} onChange={(v) => set("photo_base64", v)} />
        </div>
      )}
    </div>
  );
};

/** Anfitrión: catálogo de la instalación o texto libre. */
export const HostField = ({ companyId, facilityId, hostId, hostName, onChange }) => {
  const { t } = useTranslation();
  const [hosts, setHosts] = useState([]);

  useEffect(() => {
    if (!companyId || companyId === "all") return;
    visitorApi
      .get("/hosts", { company_id: companyId, facility_id: facilityId })
      .then(setHosts)
      .catch(() => setHosts([]));
  }, [companyId, facilityId]);

  const selectValue = hostId || (hostName ? "__other" : "__none");

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <Field label={t("visitors.common.host")}>
        <Select
          value={selectValue}
          onValueChange={(v) => {
            if (v === "__other") onChange({ host_id: null, host_name: hostName || "" });
            else if (v === "__none") onChange({ host_id: null, host_name: null });
            else onChange({ host_id: v, host_name: hosts.find((h) => h.id === v)?.name });
          }}
        >
          <SelectTrigger>
            <SelectValue placeholder={t("visitors.common.selectHost")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__none">{t("visitors.common.none")}</SelectItem>
            {hosts.map((h) => (
              <SelectItem key={h.id} value={h.id}>
                {h.name}
                {h.unit ? ` · ${h.unit}` : ""}
              </SelectItem>
            ))}
            <SelectItem value="__other">{t("visitors.common.otherHost")}</SelectItem>
          </SelectContent>
        </Select>
      </Field>
      {selectValue === "__other" && (
        <Field label={t("visitors.common.hostName")}>
          <Input value={hostName || ""} onChange={(e) => onChange({ host_id: null, host_name: e.target.value })} />
        </Field>
      )}
    </div>
  );
};

export const VehicleFields = ({ value, onChange }) => {
  const { t } = useTranslation();
  const v = value || {};
  const set = (k, val) => onChange({ ...v, [k]: val });
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      <Field label={`${t("visitors.common.plate")} *`}>
        <Input value={v.plate || ""} onChange={(e) => set("plate", e.target.value.toUpperCase())} />
      </Field>
      <Field label={t("visitors.common.make")}>
        <Input value={v.make || ""} onChange={(e) => set("make", e.target.value)} />
      </Field>
      <Field label={t("visitors.common.model")}>
        <Input value={v.model || ""} onChange={(e) => set("model", e.target.value)} />
      </Field>
      <Field label={t("visitors.common.color")}>
        <Input value={v.color || ""} onChange={(e) => set("color", e.target.value)} />
      </Field>
    </div>
  );
};

export const ItemsEditor = ({ items, onChange }) => {
  const { t } = useTranslation();
  const update = (i, k, v) => onChange(items.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));
  return (
    <div className="space-y-2">
      {items.map((item, i) => (
        <div key={i} className="grid grid-cols-12 gap-2 items-end">
          <Field label={t("visitors.common.item")} className="col-span-12 sm:col-span-4">
            <Input value={item.description} onChange={(e) => update(i, "description", e.target.value)} />
          </Field>
          <Field label={t("visitors.common.category")} className="col-span-6 sm:col-span-3">
            <Select value={item.category || "other"} onValueChange={(v) => update(i, "category", v)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ITEM_CATEGORIES.map((k) => (
                  <SelectItem key={k} value={k}>
                    {t(`visitors.itemCategories.${k}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label={t("visitors.common.serial")} className="col-span-6 sm:col-span-2">
            <Input value={item.serial || ""} onChange={(e) => update(i, "serial", e.target.value)} />
          </Field>
          <Field label={t("visitors.common.quantity")} className="col-span-8 sm:col-span-2">
            <Input
              type="number"
              min={1}
              value={item.quantity}
              onChange={(e) => update(i, "quantity", Math.max(1, parseInt(e.target.value) || 1))}
            />
          </Field>
          <div className="col-span-4 sm:col-span-1 flex justify-end">
            <Button type="button" variant="ghost" size="icon" onClick={() => onChange(items.filter((_, idx) => idx !== i))}>
              <Trash2 className="h-4 w-4 text-red-500" />
            </Button>
          </div>
        </div>
      ))}
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => onChange([...items, { description: "", category: "other", serial: "", quantity: 1 }])}
      >
        <Plus className="h-4 w-4 mr-2" />
        {t("visitors.access.addItem")}
      </Button>
    </div>
  );
};

const visitorPayload = (v) => ({
  ...v,
  id_number: v.id_number?.trim() || null,
  visitor_company: v.visitor_company || null,
  phone: v.phone || null,
  email: v.email || null,
});

/**
 * Registro de entrada. preset puede traer { preauth, visitor, qrToken } desde el escáner o las preautorizaciones.
 */
export const CheckInDialog = ({ open, onOpenChange, scope, preset, onDone }) => {
  const { t } = useTranslation();
  const { getErrorMessage } = useApiMessages();
  const preauth = preset?.preauth;
  const [mode, setMode] = useState("existing");
  const [visitor, setVisitor] = useState(null);
  const [newVisitor, setNewVisitor] = useState(EMPTY_VISITOR);
  const [form, setForm] = useState({});
  const [withVehicle, setWithVehicle] = useState(false);
  const [vehicle, setVehicle] = useState(null);
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [denyOpen, setDenyOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const defaultFacility = preauth?.facility_id || (scope.facilityId !== "all" ? scope.facilityId : scope.facilities[0]?.id);
    setMode("existing");
    setVisitor(preset?.visitor || null);
    setNewVisitor(EMPTY_VISITOR);
    setForm({
      facility_id: defaultFacility || "",
      access_point: "",
      host_id: preauth?.host_id || null,
      host_name: preauth?.host_name || null,
      destination: preauth?.destination || "",
      purpose: preauth?.purpose || "",
      id_verified: false,
      notes: "",
    });
    setWithVehicle(!!preauth?.vehicle);
    setVehicle(preauth?.vehicle || null);
    setItems([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const facility = scope.facilities.find((f) => f.id === form.facility_id);
  const companyId = facility?.company_id || scope.effectiveCompanyId;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const currentVisitorData = () => {
    if (preauth) return { visitor_id: preauth.visitor_id };
    if (mode === "existing") return { visitor_id: visitor?.id };
    return { visitor: visitorPayload(newVisitor) };
  };

  const handleSubmit = async () => {
    if (!form.facility_id) return toast.error(t("visitors.common.selectFacility"));
    if (!preauth && mode === "existing" && !visitor) return toast.error(t("visitors.access.selectVisitorFirst"));
    if (!preauth && mode === "new" && !newVisitor.full_name.trim()) return toast.error(t("visitors.access.nameRequired"));
    if (withVehicle && !vehicle?.plate) return toast.error(t("visitors.access.plateRequired"));
    if (facility?.require_id_verification && !form.id_verified) return toast.error(t("visitors.access.idVerificationRequired"));
    setSaving(true);
    try {
      const visit = await visitorApi.post("/visits/check-in", {
        ...form,
        ...currentVisitorData(),
        access_point: form.access_point || null,
        preauth_id: preset?.qrToken ? null : preauth?.id,
        qr_token: preset?.qrToken || null,
        vehicle: withVehicle ? vehicle : null,
        items: items.filter((i) => i.description.trim()),
      });
      toast.success(t("visitors.access.entryRegistered", { name: visit.visitor_name }));
      onOpenChange(false);
      printVisitLabel(visit, t);
      onDone?.(visit);
    } catch (error) {
      toast.error(getErrorMessage(error, "visitors.errors.saveError"));
    } finally {
      setSaving(false);
    }
  };

  const denyPreset = {
    facility_id: form.facility_id,
    access_point: form.access_point,
    ...currentVisitorData(),
    host_name: form.host_name,
    purpose: form.purpose,
    preauth_id: preauth?.id,
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <LogIn className="h-5 w-5" />
              {t("visitors.access.newEntry")}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-5 py-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label={`${t("visitors.common.facility")} *`}>
                <Select value={form.facility_id} onValueChange={(v) => set("facility_id", v)} disabled={!!preauth}>
                  <SelectTrigger>
                    <SelectValue placeholder={t("visitors.common.selectFacility")} />
                  </SelectTrigger>
                  <SelectContent>
                    {scope.facilities.map((f) => (
                      <SelectItem key={f.id} value={f.id}>
                        {f.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              {facility?.access_points?.length > 0 && (
                <Field label={t("visitors.common.accessPoint")}>
                  <Select value={form.access_point || "__none"} onValueChange={(v) => set("access_point", v === "__none" ? "" : v)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none">{t("visitors.common.none")}</SelectItem>
                      {facility.access_points.map((p) => (
                        <SelectItem key={p} value={p}>
                          {p}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              )}
            </div>

            <section className="space-y-3">
              <h3 className="font-semibold text-slate-900">{t("visitors.common.visitor")}</h3>
              {preauth ? (
                <div className="rounded-lg border bg-slate-50 p-3">
                  <p className="font-medium">{preauth.visitor_name}</p>
                  <p className="text-sm text-slate-500">
                    {[preauth.visitor_id_number, preauth.visitor_company].filter(Boolean).join(" · ")}
                  </p>
                </div>
              ) : (
                <>
                  <Tabs value={mode} onValueChange={setMode}>
                    <TabsList>
                      <TabsTrigger value="existing">{t("visitors.access.existingVisitor")}</TabsTrigger>
                      <TabsTrigger value="new">{t("visitors.access.newVisitorTab")}</TabsTrigger>
                    </TabsList>
                  </Tabs>
                  {mode === "existing" &&
                    (visitor ? (
                      <div className="rounded-lg border p-3 flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3 min-w-0">
                          {visitor.photo_base64 && (
                            <img src={visitor.photo_base64} alt="" className="h-12 w-12 rounded-full object-cover" />
                          )}
                          <div className="min-w-0">
                            <p className="font-medium truncate">{visitor.full_name}</p>
                            <p className="text-sm text-slate-500 truncate">
                              {[visitor.id_number, visitor.visitor_company].filter(Boolean).join(" · ")}
                            </p>
                            {visitor.is_blocked && (
                              <Badge variant="destructive" className="mt-1">
                                {t("visitors.registry.blockedWarning")}
                              </Badge>
                            )}
                          </div>
                        </div>
                        <Button variant="outline" size="sm" onClick={() => setVisitor(null)}>
                          {t("visitors.access.change")}
                        </Button>
                      </div>
                    ) : (
                      <VisitorSearch companyId={companyId} onSelect={setVisitor} />
                    ))}
                  {mode === "new" && <VisitorFields value={newVisitor} onChange={setNewVisitor} />}
                </>
              )}
            </section>

            <section className="space-y-3">
              <h3 className="font-semibold text-slate-900">{t("visitors.access.visitData")}</h3>
              <HostField
                companyId={companyId}
                facilityId={form.facility_id}
                hostId={form.host_id}
                hostName={form.host_name}
                onChange={(h) => setForm((f) => ({ ...f, ...h }))}
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label={t("visitors.common.destination")}>
                  <Input value={form.destination} onChange={(e) => set("destination", e.target.value)} />
                </Field>
                <Field label={t("visitors.common.purpose")}>
                  <Input value={form.purpose} onChange={(e) => set("purpose", e.target.value)} />
                </Field>
              </div>
            </section>

            <section className="space-y-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox checked={withVehicle} onCheckedChange={(c) => setWithVehicle(!!c)} />
                <Car className="h-4 w-4 text-slate-500" />
                <span className="font-medium">{t("visitors.access.addVehicle")}</span>
              </label>
              {withVehicle && <VehicleFields value={vehicle} onChange={setVehicle} />}
            </section>

            <section className="space-y-3">
              <h3 className="font-semibold text-slate-900">{t("visitors.common.items")}</h3>
              <ItemsEditor items={items} onChange={setItems} />
            </section>

            <Field label={t("visitors.common.notes")}>
              <Textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} />
            </Field>

            <label
              className={`flex items-center gap-2 rounded-lg border p-3 cursor-pointer ${
                facility?.require_id_verification ? "border-amber-300 bg-amber-50" : ""
              }`}
            >
              <Checkbox checked={form.id_verified} onCheckedChange={(c) => set("id_verified", !!c)} />
              <UserCheck className="h-4 w-4 text-slate-600" />
              <span className="text-sm">
                {t("visitors.access.idVerified")}
                {facility?.require_id_verification && <strong> *</strong>}
              </span>
            </label>
          </div>

          <DialogFooter className="gap-2 sm:justify-between">
            <Button variant="outline" className="text-red-600 border-red-200" onClick={() => setDenyOpen(true)}>
              <Ban className="h-4 w-4 mr-2" />
              {t("visitors.access.denyEntry")}
            </Button>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                {t("visitors.common.cancel")}
              </Button>
              <Button onClick={handleSubmit} disabled={saving}>
                <LogIn className="h-4 w-4 mr-2" />
                {t("visitors.access.registerEntry")}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <DenyDialog
        open={denyOpen}
        onOpenChange={setDenyOpen}
        preset={denyPreset}
        visitorName={preauth?.visitor_name || visitor?.full_name || newVisitor.full_name}
        onDone={(v) => {
          onOpenChange(false);
          onDone?.(v);
        }}
      />
    </>
  );
};

/** Rechazo de ingreso con motivo; opcionalmente genera un incidente. */
export const DenyDialog = ({ open, onOpenChange, preset, visitorName, onDone }) => {
  const { t } = useTranslation();
  const { getErrorMessage } = useApiMessages();
  const [reason, setReason] = useState("");
  const [createIncident, setCreateIncident] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setReason("");
      setCreateIncident(true);
    }
  }, [open]);

  const handleDeny = async () => {
    if (!reason.trim()) return toast.error(t("visitors.access.denyReasonRequired"));
    if (!preset.facility_id) return toast.error(t("visitors.common.selectFacility"));
    if (!preset.visitor_id && !preset.visitor?.full_name?.trim()) return toast.error(t("visitors.access.selectVisitorFirst"));
    setSaving(true);
    try {
      const visit = await visitorApi.post("/visits/deny", { ...preset, reason, create_incident: createIncident });
      toast.success(t("visitors.access.entryDenied"));
      onOpenChange(false);
      onDone?.(visit);
    } catch (error) {
      toast.error(getErrorMessage(error, "visitors.errors.saveError"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("visitors.access.denyTitle")}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {visitorName && <p className="font-medium">{visitorName}</p>}
          <Field label={`${t("visitors.access.denyReason")} *`}>
            <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} />
          </Field>
          <label className="flex items-center gap-2 cursor-pointer">
            <Checkbox checked={createIncident} onCheckedChange={(c) => setCreateIncident(!!c)} />
            <span className="text-sm">{t("visitors.access.createIncident")}</span>
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("visitors.common.cancel")}
          </Button>
          <Button variant="destructive" onClick={handleDeny} disabled={saving}>
            <Ban className="h-4 w-4 mr-2" />
            {t("visitors.access.denyEntry")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/** Registro de incidente; puede venir vinculado a una visita. */
export const IncidentDialog = ({ open, onOpenChange, scope, visit, defaultType = "other", onDone }) => {
  const { t } = useTranslation();
  const { getErrorMessage } = useApiMessages();
  const [form, setForm] = useState({});
  const [photos, setPhotos] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({
      facility_id: visit?.facility_id || (scope.facilityId !== "all" ? scope.facilityId : scope.facilities[0]?.id) || "",
      incident_type: defaultType,
      severity: "medium",
      description: "",
      visitor_name: visit?.visitor_name || "",
    });
    setPhotos([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.facility_id) return toast.error(t("visitors.common.selectFacility"));
    if (!form.description.trim()) return toast.error(t("visitors.incidents.descriptionRequired"));
    setSaving(true);
    try {
      const incident = await visitorApi.post("/incidents", {
        ...form,
        visitor_name: form.visitor_name || null,
        visit_id: visit?.id || null,
        visitor_id: visit?.visitor_id || null,
        photos,
      });
      toast.success(t("visitors.incidents.created"));
      onOpenChange(false);
      onDone?.(incident);
    } catch (error) {
      toast.error(getErrorMessage(error, "visitors.errors.saveError"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("visitors.incidents.newIncident")}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {!visit && (
            <Field label={`${t("visitors.common.facility")} *`}>
              <Select value={form.facility_id} onValueChange={(v) => set("facility_id", v)}>
                <SelectTrigger>
                  <SelectValue placeholder={t("visitors.common.selectFacility")} />
                </SelectTrigger>
                <SelectContent>
                  {scope.facilities.map((f) => (
                    <SelectItem key={f.id} value={f.id}>
                      {f.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          )}
          <div className="grid grid-cols-2 gap-3">
            <Field label={t("visitors.incidents.type")}>
              <Select value={form.incident_type} onValueChange={(v) => set("incident_type", v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {INCIDENT_TYPES.map((k) => (
                    <SelectItem key={k} value={k}>
                      {t(`visitors.incidentTypes.${k}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label={t("visitors.incidents.severity")}>
              <Select value={form.severity} onValueChange={(v) => set("severity", v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SEVERITIES.map((k) => (
                    <SelectItem key={k} value={k}>
                      {t(`visitors.severity.${k}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>
          <Field label={t("visitors.common.visitor")}>
            <Input value={form.visitor_name || ""} onChange={(e) => set("visitor_name", e.target.value)} disabled={!!visit} />
          </Field>
          <Field label={`${t("visitors.incidents.description")} *`}>
            <Textarea rows={4} value={form.description || ""} onChange={(e) => set("description", e.target.value)} />
          </Field>
          <div>
            <Label className="mb-1 block">{t("visitors.incidents.photos")}</Label>
            <div className="flex flex-wrap gap-3">
              {photos.map((p, i) => (
                <PhotoInput key={i} value={p} onChange={() => setPhotos(photos.filter((_, idx) => idx !== i))} />
              ))}
              {photos.length < 5 && <PhotoInput value={null} onChange={(p) => p && setPhotos([...photos, p])} />}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("visitors.common.cancel")}
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {t("visitors.common.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
